import React, { FC, useState, useEffect, useRef } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { IMultiselect, OptionType } from 'core/interface/autocomplete';
import Image from 'components/image/Image';
import { useField } from 'formik';

import './Index.scss';
import useLocales from 'hooks/useLocales';

const Multiselect: FC<IMultiselect> = ({
  options,
  placeholder,
  className,
  onChange,
  onInputChange,
  value,
  fieldName,
  freeSolo,
  loadMore,
  show_location = false
}): JSX.Element => {
  const { t } = useLocales();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const multiselectRef = useRef<HTMLDivElement>(null);
  const listboxRef = useRef<HTMLUListElement | null>(null);
  const [scrollPosition, setScrollPosition] = useState<number>(0);
  const [field, meta] = useField(fieldName);

  const toggleArrow = () => {
    setIsOpen((prev) => !prev);
  };

  useEffect(() => {
    handleReset();
  }, [value]);

  const handleReset = () => {};

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const targetElement = event.target as Element;

      if (
        multiselectRef.current &&
        !multiselectRef.current.contains(targetElement) &&
        !targetElement.classList.contains('MuiAutocomplete-option')
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [multiselectRef]);

  const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      const input = event.target as HTMLInputElement;
      const newValue = input.value.trim();
      if (newValue) {
        onChange && onChange([...(value || []), newValue]);
        input.value = '';
      }
      event.preventDefault();
    }
  };

  const handleScroll = (event: any) => {
    const bottom =
      event.target.scrollHeight - event.target.scrollTop ===
      event.target.clientHeight;
    if (bottom) {
      setScrollPosition(event.target.scrollTop);
      loadMore && loadMore();
    }
  };

  useEffect(() => {
    if (listboxRef.current) {
      listboxRef.current.scrollTop = scrollPosition;
    }
  }, [options]);

  return (
    <div className="multi" ref={multiselectRef}>
      <Autocomplete
        {...field}
        onReset={handleReset}
        className={`multisel customAutocomplete ${className}`}
        multiple
        freeSolo={freeSolo}
        open={isOpen}
        onOpen={toggleArrow}
        onClose={toggleArrow}
        options={options || []}
        clearIcon={false}
        value={value}
        ListboxProps={{
          className: 'scrollable',
          onScroll: handleScroll,
          ref: listboxRef
        }}
        getOptionLabel={(option) => (option as OptionType).title ?? ''}
        // onInputChange={(e) => {
        //   const value =
        //     (e as unknown as React.ChangeEvent<HTMLInputElement>)?.target
        //       ?.value || (e as unknown as string);
        //   onInputChange && onInputChange(value);
        // }}
        onInputChange={(e: any) => {
          onInputChange && onInputChange(e?.target?.value || e);
        }}
        onChange={(_, value) =>
          onChange && onChange(value as string | OptionType[])
        }
        renderInput={(params) => (
          <TextField
            key={params.id}
            {...params}
            placeholder={t(placeholder) || placeholder}
            onKeyDown={handleKeyPress}
          />
        )}
      />
      {options && (
        <span onClick={() => setIsOpen(true)}>
          <Image
            className={`arrow-down ${isOpen ? 'active' : ''}`}
            name="down-arrow.svg"
          />
        </span>
      )}
    </div>
  );
};

export default Multiselect;
